import {AfterViewInit, Component, ElementRef, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {EmailTicketModalComponent} from '@app/layout/sites/tickets/email-ticket-modal/email-ticket-modal.component';
import {TicketModal} from '@app/layout/sites/tickets/ticket-modals/ticket-modal-util';
import {TicketTrackingEvent, TicketTrackingService} from '@app/layout/sites/tickets/ticket-tracking.service';
import {SplitLastWordParts, TicketUtils} from '@app/layout/sites/tickets/ticket-utils';
import {ESCALATED_TICKETS_EXECUTABLE_ACTIONS_BY_STATUS_ID, EscalatedTicketExecutableAction, TicketComponentPart, TicketOperation, TicketOperationResult, TicketUnassignedOption, UNASSIGNED_ID} from '@app/layout/sites/tickets/tickets.interface';
import {BootstrapService, EmailTrackingCampaignName, EscalatedTicket, MediaQueryType, MessageTranslateService, Ticket, TicketAPIResultPartial, TicketBenefit, TicketBenefitId, ticketBenefitsById, TicketPriorityId, TicketReferenceData, TicketsService, TicketStatusId, TicketType} from '@app/shared/services';
import {UserEntity} from '@app/shared/services/api/user-entity';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {I18nMessage} from '@shared/api/model/i18n';
import {AceSelectComponent} from '@shared/components/ace-select/ace-select.component';
import {EmailType} from '@app/layout/sites/tickets/email-ticket/email-ticket.component';
import {EllipsisComponent} from "@shared/components/ellipsis/ellipsis.component";
import {APP_ICON_ROTATE} from '@shared/components/icon/icon.component';
import {ToastType} from '@shared/components/toaster/Toast';
import {ToasterService} from '@shared/components/toaster/toaster.service';
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {BrowserUtils} from '@shared/util/general/browser-utils';
import {EMPTY, map, Observable, Subject, Subscription} from 'rxjs';
import {finalize, takeUntil, tap} from 'rxjs/operators';
import {TicketActionStateManager} from '../ticket-action-state-manager';


interface BoardAssigneeEntity {
  entity: UserEntity;
  users: User[];
}


@Component({
  selector: 'app-escalated-ticket-summary',
  templateUrl: './escalated-ticket-summary.component.html',
  styleUrls: ['./escalated-ticket-summary.component.scss'],
  standalone: false
})
export class EscalatedTicketSummaryComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy, TicketComponentPart {

  @Input() ticket: EscalatedTicket;
  // this flag dictates if the component supports editing ticket assignees
  @Input() canEditTicketAssignee: boolean;
  @Input() grabFocus: { focus: boolean, options?: FocusOptions } = { focus: false };
  @Input() showSiteName = false;
  @Input() showSiteNameAsLink = true;
  @Input() showOtherEscalatedTicketsCount = false;
  @Input() cardSummaryOnly = true;
  @Input() showFirstComment = false;
  @Input() readOnly = false;

  @Output() ticketOperation: EventEmitter<TicketOperationResult<EscalatedTicket>> = new EventEmitter();

  @ViewChild('approveButton', { static: false }) approveButton: ElementRef;
  @ViewChild('aceSelectComponent', { static: false }) aceSelectComponent: AceSelectComponent;
  @ViewChild('summaryEllipsis', { static: false }) summaryEllipsis: EllipsisComponent;

  readonly COPY_TO_CLIPBOARD_ALERT_TIMEOUT = 2000;
  readonly ICON_ROTATE = APP_ICON_ROTATE;
  readonly mediaMatcher: MediaQueryList;
  readonly getActionLinkedAlerts = TicketUtils.getActionLinkedAlerts;
  readonly TicketStatusId = TicketStatusId;


  private readonly TRANSITIONABLE_TICKET_STATUSES = new Set([
    TicketStatusId.STATUS_NEW,
    TicketStatusId.STATUS_ON_HOLD,
    TicketStatusId.STATUS_NOT_DOING
  ]);
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly benefitsById: Map<TicketBenefitId, TicketBenefit> = ticketBenefitsById;
  private readonly logger: AceLogger;

  private alertTimeout: NodeJS.Timeout;
  private modal: TicketModal<EscalatedTicket>;
  private ticketModalInjector: Injector;
  private otherEscalatedTicketsCountSubscription: Subscription;

  readonly unassignedOption: TicketUnassignedOption = {
    id: UNASSIGNED_ID,
    fullName: this.translateService.instant('Unassigned')
  };
  readonly ExecutableAction = EscalatedTicketExecutableAction;

  TicketType = TicketType;
  boardAssigneeGroups: BoardAssigneeEntity[] = [];
  assigneeOptions: User[];
  assigneesById: Map<string, User>;
  assigneesByEmail: Map<string, User>;
  assigneeEditable = false;
  assigneeSaving = false;
  assigneeControl: UntypedFormControl;
  showCopyLink = false;
  copyAlertMessage: I18nMessage;
  assigneesLoading: boolean;
  benefitName: string;
  otherEscalatedTicketsCount: number;

  constructor(private ticketsService: TicketsService,
              private ticketTrackingService: TicketTrackingService,
              private modalService: NgbModal,
              private toasterService: ToasterService,
              private translateService: MessageTranslateService,
              private ticketDataCaches: TicketDataCaches,
              private injector: Injector,
              @Optional() private actionStateManager: TicketActionStateManager<EscalatedTicketExecutableAction>,
              bootstrapService: BootstrapService,
              logService: AceLoggerService) {
    this.actionStateManager ??= new TicketActionStateManager(ESCALATED_TICKETS_EXECUTABLE_ACTIONS_BY_STATUS_ID);
    this.logger = logService.create(EscalatedTicketSummaryComponent.name);
    this.mediaMatcher = bootstrapService.getBreakpointMediaQueryList('lg', MediaQueryType.MAX_WIDTH);
  }

  ngOnInit(): void {
    this.assigneeControl = new UntypedFormControl();
    this.setAssigneeControlValue(this.ticketAssignee);
    this.assigneeControl.valueChanges
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((assigneeId: string) => this.onAssigneeSelected(assigneeId !== UNASSIGNED_ID ? assigneeId : null));
  }

  ngAfterViewInit(): void {
    if (this.grabFocus && this.grabFocus.focus && this.approveButton && this.approveButton.nativeElement instanceof HTMLElement) {
      this.approveButton.nativeElement.focus(this.grabFocus.options);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeOtherEscalatedTicketCount();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refreshAssignees();
    if (changes.ticket) {
      this.benefitName = this.benefitsById?.get(this.ticket?.benefit[0]?.benefit_id)?.benefit_name ?? null;
      this.watchOtherEscalatedTicketCount();
    }
  }

  private onTicketOperation(result: TicketAPIResultPartial<EscalatedTicket>) {
    this.ticketOperation.emit({
      operation: TicketOperation.UPDATE,
      ticket: this.ticket,
      affectedTickets: result.affected_tickets
    });
  }

  private onAssigneeSelected(assigneeId: string | null) {
    const currentAssignee = this.ticketAssignee;
    const currentAssigneeId = currentAssignee ? currentAssignee.id : null;

    if (assigneeId != null && !this.assigneesById.has(assigneeId)) {
      this.toasterService.push({
        text: null,
        message: { messageKey: 'Cannot select assignee with id, as the assignee has been deleted.', parameters: { assigneeId } },
        type: ToastType.Danger,
        dismissible: true,
        timeout: 0,
        error: true
      });
      this.assigneeControl.setValue(currentAssigneeId, { emitEvent: false });
      return;
    }

    if (assigneeId !== currentAssigneeId) {
      this.assigneeSaving = true;
      this.ticketsService.assignEscalatedTicket(this.ticket, assigneeId, this.ticketReferenceData)
        .subscribe({
          next: (result) => {
            this.onTicketOperation(result);
            this.ticketTrackingService.trackTicket(TicketTrackingEvent.ASSIGN, this.ticket);
          },
          error: ({ error }) => {
            // flip the value back to original
            const previousAssignee = this.ticketAssignee;
            this.assigneeControl.patchValue(previousAssignee && previousAssignee.id, { emitEvent: false });
            // maybe we need a more sophisticated error message
            throw new Error('Failed to assignee ticket, error: ' + (error.message ? error.message : JSON.stringify(error)));
          }
        })
        .add(() => {
          this.assigneeEditable = false;
          this.assigneeSaving = false;
        });
    }
  }

  private registerAssigneeAndEntity(assignee: User, entitiesById: Map<number, BoardAssigneeEntity>): void {
    const entity = assignee.entity;
    const entityId = entity.id;
    const existingEntity = entitiesById.get(entityId);
    if (existingEntity) {
      existingEntity.users.push(assignee);
    } else {
      entitiesById.set(entityId, { entity, users: [assignee] });
    }
  }

  private populateAssigneeSelectOptions() {
    if (this.canEditTicketAssignee && this.assigneesById) {
      const currentAssignee = this.ticketAssignee;
      const hasCurrentAssignee = currentAssignee != null;
      const hasAssigneeList = this.assigneesById && this.assigneesById.size;
      const currentAssigneeDeleted = this.isCurrentAssigneeDeleted;

      if (hasCurrentAssignee || hasAssigneeList) {
        // only populate select options if:
        // 1. ticket has current assignee (can be unassigned)
        // 2. have a list of assignees
        const entitiesById: Map<number, BoardAssigneeEntity> = new Map();

        // if assignee list is provided, iterate over it and register assignees and their entities
        if (hasAssigneeList) {
          this.assigneesById.forEach((user: User) => this.registerAssigneeAndEntity(user, entitiesById));
        }

        // if current ticket assignee does not exist in the assignee list
        // register the user entity and the user
        if (hasCurrentAssignee && !this.assigneesById.has(currentAssignee.id) && !currentAssigneeDeleted) {
          this.registerAssigneeAndEntity(currentAssignee, entitiesById);
        }

        this.boardAssigneeGroups = Array.from(entitiesById.values());

        this.assigneeOptions = [<User>this.unassignedOption];
        if (currentAssigneeDeleted) {
          this.assigneeOptions.push(currentAssignee);
        }

        if (!BrowserUtils.isMobile()) {
          // assigneeOptions are only needed when ng-select is used
          this.boardAssigneeGroups.forEach((entity: BoardAssigneeEntity) => {
            this.assigneeOptions = this.assigneeOptions.concat(entity.users);
          });
        }
      }
    }

  }

  private refreshAssignees() {
    this.assigneesLoading = true;
    this.ticketDataCaches.getAssigneesBySiteId(this.ticket.site_id)
      .pipe(finalize(() => this.assigneesLoading = false))
      .subscribe(assigneesById => {
        this.assigneesById = assigneesById;
        this.populateAssigneeSelectOptions();
      });
    /** getting assignees by email separate from getting assignees by id to avoid creating a new map for each ticket */
    this.ticketDataCaches.getAssigneesByEmailBySiteId(this.ticket.site_id)
      .subscribe(assigneesByEmail => {
        this.assigneesByEmail = assigneesByEmail;
      });
  }

  get ticketAssignee(): User | null {
    return this.ticket?.assignees?.length ? this.ticket.assignees[0] : null;
  }

  get ticketReferenceData(): TicketReferenceData {
    return {
      assigneesById: this.assigneesById
    };
  }

  get isCurrentAssigneeDeleted(): boolean {
    const assignee = this.ticketAssignee;
    return assignee && !(assignee.firstname || assignee.lastname);
  }

  get isMobile(): boolean {
    return BrowserUtils.isMobile();
  }

  get summaryParts(): SplitLastWordParts {
    return TicketUtils.splitLastWord(this.ticket.summary);
  }

  get fullNameParts(): SplitLastWordParts {
    return TicketUtils.splitLastWord((this.ticketAssignee || this.unassignedOption).fullName);
  }

  updatedOrResolved(): I18nMessage {
    switch (this.ticket?.status?.status_id) {
      case (TicketStatusId.STATUS_IN_PROGRESS):
      case (TicketStatusId.STATUS_ON_HOLD):
        return {messageKey: 'Updated'};
      case (TicketStatusId.STATUS_RESOLVED):
      case (TicketStatusId.STATUS_NOT_DOING):
        return {messageKey: 'Resolved'};
    }
  }

  showPriority(priorityId: number): boolean {
   return TicketPriorityId.isHighPriority(priorityId);
  }

  onClickApprove(event: MouseEvent) {
    event.stopPropagation();
    this.ticketTrackingService.trackTicket(TicketTrackingEvent.APPROVED, this.ticket);
    const modalRef = this.modalService.open(EmailTicketModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: true,
      size: 'lg',
      injector: this.getTicketModalInjector()
    });
    const modalInstance: EmailTicketModalComponent = modalRef.componentInstance;
    modalInstance.ticket = this.ticket;
    modalInstance.emailType = EmailType.APPROVE;
    modalInstance.campaignName = EmailTrackingCampaignName.APPROVE;
    modalInstance.beforeClose = () => {
      if (this.TRANSITIONABLE_TICKET_STATUSES.has(this.ticket.status.status_id)) {
        return this.updateTicketStatus(TicketStatusId.STATUS_IN_PROGRESS);
      } else if (this.ticket.inbox && this.ticket.status.status_id === TicketStatusId.STATUS_IN_PROGRESS) {
        return this.updateTicketInbox(false);
      }
      return EMPTY;
    };
    this.modal = modalRef.componentInstance;
    modalRef.result
      .then(
        () => this.toasterService.push({message: {messageKey: 'Email Sent'}, type: ToastType.Success}),
        // ignore if the modal was cancelled
        () => {})
      .finally(() => this.modal = null);
  }

  updateStatus(action: EscalatedTicketExecutableAction, event: MouseEvent) {
    event.stopPropagation();
    this.actionStateManager.setExecutingAction(action, true);
    const statusBeforeUpdate = this.ticket.status;
    this.updateTicketStatus(action.valueOf())
      .pipe(finalize(() => this.actionStateManager.setExecutingAction(action, false)))
      .subscribe(result => {
        const updatedTicketPart = result.ticket;
        this.ticketTrackingService.trackTicket(TicketTrackingEvent.STATUS_MOVED, this.ticket, {
          status_from: statusBeforeUpdate.display_text,
          status_to: updatedTicketPart.status.display_text
        });
      });
  }

  isActionInProgress(): boolean {
    return this.actionStateManager.isActionInProgress();
  }

  isActionExecuting(action: EscalatedTicketExecutableAction): boolean {
    return this.actionStateManager.isActionExecuting(action);
  }

  isActionAvailable(action: EscalatedTicketExecutableAction): boolean {
    return this.actionStateManager.isActionAvailable(action, this.ticket?.status?.status_id ?? null);
  }

  shouldDisplayTicketStatus(): boolean {
    return !this.cardSummaryOnly || this.ticket?.status?.status_id !== TicketStatusId.STATUS_IN_PROGRESS;
  }

  shouldDisplayTicketAssignee(): boolean {
    if (this.cardSummaryOnly) {
      const statusId = this.ticket?.status?.status_id;
      return statusId === TicketStatusId.STATUS_IN_PROGRESS || (statusId === TicketStatusId.STATUS_NEW && !!this.ticket.assignees?.length);
    } else {
      return true;
    }
  }

  private getTicketModalInjector(): Injector {
    if (!this.ticketModalInjector) {
      this.ticketModalInjector = TicketUtils.makeTicketDataCachesInjector(this.ticketDataCaches, this.injector);
    }
    return this.ticketModalInjector;
  }

  private updateTicketStatus(statusId: TicketStatusId): Observable<TicketAPIResultPartial<EscalatedTicket>> {
    return this.ticketsService.updateTicketStatus(this.ticket, statusId, this.ticketReferenceData).pipe(
        tap((result) => {
          this.onTicketOperation(result);
        })
    );
  }

  private updateTicketInbox(inboxValue: boolean): Observable<TicketAPIResultPartial<EscalatedTicket>> {
    return this.ticketsService.updateTicketInbox(this.ticket, inboxValue, this.ticketReferenceData).pipe(
        tap((result) => {
          this.onTicketOperation(result);
        }));
  }

  assigneeClicked(): void {
    this.setAssigneeEditable(true);
  }

  groupByFn(assignee: User): string | null {
    const entity = assignee.entity;
    return entity ? entity.name : null;
  }

  // this flag dictates if the select can be enabled
  canUpdateTicketAssignee(): boolean {
    return this.canEditTicketAssignee
        // allow to show select when there are options to choose from or the current ticket is assigned (allow to be unassigned)
        && (this.boardAssigneeGroups && this.boardAssigneeGroups.length > 0 || this.ticketAssignee != null);
  }

  setAssigneeEditable(editable: boolean) {
    if (!editable || this.canUpdateTicketAssignee()) {
      // only allow to change assignee editable status if the component can do so also there is at least one assignee in the assignees list
      this.assigneeEditable = editable;
      if (editable) {
        setTimeout(() => {
          // set timeout to make sure aceSelectComponent can be located
          if (this.aceSelectComponent) {
            this.aceSelectComponent.open();
          }
        }, 0);
      }
    }
  }

  toggleAssigneeEditable() {
    this.setAssigneeEditable(!this.assigneeEditable);
  }

  private setAssigneeControlValue(assignee: User | null): void {
    this.assigneeControl.setValue(assignee ? assignee.id : UNASSIGNED_ID, { emitEvent: false });
  }

  afterTicketUpdate(result: TicketOperationResult<Ticket>): void {
    this.setAssigneeControlValue(this.ticketAssignee);
    this.checkAndReWatchOtherEscalatedTicketCount(result);
  }

  updateCopyLinkVisibility(visible: boolean): void {
    this.showCopyLink = visible;
  }

  copyToClipboard(text: string): void {
    BrowserUtils.copyToClipboard(text);
    this.copyAlertMessage = { messageKey: 'Copied to clipboard' };
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
    }
    this.alertTimeout = setTimeout(() => this.closeAlert(), this.COPY_TO_CLIPBOARD_ALERT_TIMEOUT);
  }

  closeModal(): void {
    this.ticketOperation.emit({ operation: TicketOperation.DISMISS, ticket: this.ticket });
  }

  closeAlert() {
    this.copyAlertMessage = null;
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
    }
  }

  getTicketsPathByTicketType(ticketType: TicketType): string {
    return TicketUtils.getPathByTicketType(ticketType);
  }

  get canShowOtherEscalatedTicketsCount(): boolean {
    return this.showOtherEscalatedTicketsCount && this.otherEscalatedTicketsCount > 0;
  }

  private checkAndReWatchOtherEscalatedTicketCount(ticketOperationResult: TicketOperationResult<Ticket>): void {
    if (TicketUtils.ticketLinkChanged(this.ticket, ticketOperationResult)) {
      this.watchOtherEscalatedTicketCount();
    }
  }

  private unsubscribeOtherEscalatedTicketCount(): void {
    if (this.otherEscalatedTicketsCountSubscription) {
      this.otherEscalatedTicketsCountSubscription.unsubscribe();
      this.otherEscalatedTicketsCountSubscription = null;
    }
  }

  private watchOtherEscalatedTicketCount(): void {
    this.unsubscribeOtherEscalatedTicketCount();
    const taskIds = TicketUtils.getTaskIdsFromActions([this.ticket]);
    if (taskIds?.length) {
      this.otherEscalatedTicketsCountSubscription = this.ticketDataCaches.watchTicketsByRuleIds(taskIds)
        .pipe(map(escalatedTicketsGroupByRuleId => TicketUtils.getOtherEscalatedTicketsCount(escalatedTicketsGroupByRuleId, this.ticket)))
        .subscribe(count => this.otherEscalatedTicketsCount = count);
    } else {
      this.otherEscalatedTicketsCount = 0;
    }
  }
}
